import React, { useState, useRef, useEffect } from 'react';

interface DropdownOption {
    value: string;
    label: string;
}

interface MaterialDropdownProps {
    options: DropdownOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

export default function MaterialDropdown({
    options,
    value,
    onChange,
    placeholder = '',
    disabled = false,
    className = ''
}: MaterialDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const selectedOption = options.find(option => option.value === value);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen && focusedIndex >= 0) {
                    onChange(options[focusedIndex].value);
                    setIsOpen(false);
                    setFocusedIndex(-1);
                } else {
                    setIsOpen(!isOpen);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setFocusedIndex(-1);
                buttonRef.current?.focus();
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setFocusedIndex(prev => 
                        prev < options.length - 1 ? prev + 1 : 0
                    );
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (isOpen) {
                    setFocusedIndex(prev => 
                        prev > 0 ? prev - 1 : options.length - 1
                    );
                }
                break;
        }
    };

    const handleOptionClick = (optionValue: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(optionValue);
        setIsOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
    };

    return (
        <div className={`material-dropdown ${className}`} ref={dropdownRef}>
            <button
                ref={buttonRef}
                type="button"
                className={`dropdown-button ${isOpen ? 'dropdown-open' : ''} ${disabled ? 'dropdown-disabled' : ''}`}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!disabled) {
                        setIsOpen(!isOpen);
                    }
                }}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={selectedOption ? selectedOption.label : placeholder}
            >
                <span className="dropdown-text">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <span className={`dropdown-arrow ${isOpen ? 'dropdown-arrow-open' : ''}`}>
                    ▼
                </span>
            </button>
            
            {isOpen && (
                <div className="dropdown-menu" role="listbox">
                    {options.map((option, index) => (
                        <div
                            key={option.value}
                            className={`dropdown-option ${value === option.value ? 'dropdown-option-selected' : ''} ${index === focusedIndex ? 'dropdown-option-focused' : ''}`}
                            onClick={(e) => handleOptionClick(option.value, e)}
                            role="option"
                            aria-selected={value === option.value}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
